/**
 * Cmarkdownflowchart Component
 * Renders a flowchart graph from mermaid flowchart syntax using React Flow.
 *
 * Camera / navigation / background chrome are shared with the mindmap via
 * Cmarkdowngraphcontrols (grid background, zoom panel, arrow pad, minimap,
 * controls overlay) so both graph widgets look and behave identically. The
 * node/edge visuals are owned elsewhere; this file owns framing + wiring only.
 */

'use client';

import type {
  CSSProperties,
  ReactElement,
} from 'react';
import { useMemo, useState } from 'react';
import {
  type Edge,
  type Node,
  type ReactFlowInstance,
} from '@xyflow/react';
import {
  Cmarkdownvisual,
  CmarkdownvisualError,
  CmarkdownvisualErrorReason,
  CmarkdownvisualType,
} from '../Cmarkdownvisual';
import { useMediaQuery } from '@/hooks/dom';
import { mediaQueries } from '@/theme/breakpoints';
import type { FlowchartNodeData, FlowchartEdgeData } from './flowchart-types';
import { FlowchartHoverContext } from './flowchart-context';
import {
  FlowchartCanvas,
  FLOWCHART_CONTAINER_HEIGHT,
} from './FlowchartCanvas';
import { FlowchartCompactPreview } from './FlowchartCompactPreview';
import { FlowchartToolbarButtons } from './FlowchartToolbarButtons';
import { useFlowchartControls } from './useFlowchartControls';
import { useFlowchartInteractions } from './useFlowchartInteractions';
import { useFlowchartLayout } from './useFlowchartLayout';
import { useFlowchartViewport } from './useFlowchartViewport';
import type { DataElScope } from '@/utils/dataEl';

const CSS_VALUE = {
  None: 'none',
} as const;

const FLOWCHART_METRIC_UNIT = 'STEPS';
const MOBILE_CONTROLS_SCALE = 0.9;

export interface CmarkdownflowchartProps {
  dataElScope: DataElScope;
  content: string;
  className?: string;
  style?: CSSProperties;
}

export function Cmarkdownflowchart({
  dataElScope,
  content,
  className,
  style,
}: CmarkdownflowchartProps): ReactElement {
  const isMobile = useMediaQuery(mediaQueries.mobile);
  const controlsScaleStyle = isMobile ? { transform: `scale(${MOBILE_CONTROLS_SCALE})` } : undefined;

  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<
    Node<FlowchartNodeData>,
    Edge<FlowchartEdgeData>
  > | null>(null);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);

  const layout = useFlowchartLayout(content);
  const {
    centerCameraOnNode,
    hasContainerSize,
    reactFlowWrapperRef,
  } = useFlowchartViewport({
    focusedNodeId,
    layout,
    reactFlowInstance,
    setFocusedNodeId,
  });
  const {
    focusNodeNeighborhood,
    handleDirectionalNavigation,
    handleNavigate,
    handlePaneClick,
    interactionState,
    onNodeClick,
    onNodeMouseEnter,
    onNodeMouseLeave,
  } = useFlowchartInteractions({
    centerCameraOnNode,
    focusedNodeId,
    layout,
    setFocusedNodeId,
  });
  const {
    handleFocusFlowchart,
    handleKeyDown,
    handleReactFlowInit,
    handleReactFlowMove,
    handleToggleMinimap,
    handleZoomChange,
    handleZoomIn,
    handleZoomOut,
    isMinimapVisible,
    zoomLevel,
  } = useFlowchartControls({
    focusedNodeId,
    focusNodeNeighborhood,
    handleDirectionalNavigation,
    layout,
    reactFlowInstance,
    setFocusedNodeId,
    setReactFlowInstance,
  });

  const nodeCount = useMemo(() => layout?.nodes.length ?? 0, [layout]);

  const compactPreview = useMemo(() => {
    return <FlowchartCompactPreview dataElScope={dataElScope} nodes={layout?.nodes ?? []} />;
  }, [dataElScope, layout]);

  if (!layout) {
    return (
      <CmarkdownvisualError
        dataElScope={dataElScope}
        type={CmarkdownvisualType.Flowchart}
        reason={CmarkdownvisualErrorReason.Unparseable}
      />
    );
  }

  if (layout.nodes.length === 0) {
    return (
      <CmarkdownvisualError
        dataElScope={dataElScope}
        type={CmarkdownvisualType.Flowchart}
        reason={CmarkdownvisualErrorReason.Empty}
      />
    );
  }

  const containerHeight = FLOWCHART_CONTAINER_HEIGHT;

  const sideColumn = (
    <FlowchartToolbarButtons
      canFocusFlowchart={reactFlowInstance != null}
      isMinimapVisible={isMinimapVisible}
      onFocusFlowchart={handleFocusFlowchart}
      onToggleMinimap={handleToggleMinimap}
    />
  );

  return (
    <Cmarkdownvisual
      dataElScope={dataElScope}
      type={CmarkdownvisualType.Flowchart}
      contentStyle={{ height: containerHeight, minHeight: containerHeight, flex: 1 }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{ outline: CSS_VALUE.None, ...style }}
      className={className}
      compactPreview={compactPreview}
      metric={{ value: nodeCount, unit: FLOWCHART_METRIC_UNIT }}
    >
      <FlowchartHoverContext.Provider value={interactionState}>
        <FlowchartCanvas
          controlsScaleStyle={controlsScaleStyle}
          dataElScope={dataElScope}
          hasContainerSize={hasContainerSize}
          isMinimapVisible={isMinimapVisible}
          layout={layout}
          reactFlowInstance={reactFlowInstance}
          reactFlowWrapperRef={reactFlowWrapperRef}
          sideColumn={sideColumn}
          zoomLevel={zoomLevel}
          onInit={handleReactFlowInit}
          onMove={handleReactFlowMove}
          onNavigate={handleNavigate}
          onNodeClick={onNodeClick}
          onNodeMouseEnter={onNodeMouseEnter}
          onNodeMouseLeave={onNodeMouseLeave}
          onPaneClick={handlePaneClick}
          onZoomChange={handleZoomChange}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
        />
      </FlowchartHoverContext.Provider>
    </Cmarkdownvisual>
  );
}
